import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IProductVideo } from "@faire/web-api/indigofair/video/IProductVideo";
import { isFastly } from "@faire/web/common/images/optimizeImages";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import isEmpty from "lodash/isEmpty";
import { useState, useMemo, useCallback } from "react";

import { PLACEHOLDER_IMAGE_URL } from "@faire/retailer/consts/PLACEHOLDER_IMAGE_URL";
import { filterInvalidImages } from "@faire/retailer/lib/filterInvalidImages";
import { canShowVideoProducts } from "@faire/retailer/views/product/ProductImages/showProductVideos";
import { IShowroomProductMedia } from "@faire/retailer/views/product/ProductImages/utils";

const PLACEHOLDER_IMAGE = IImage.build({
  token: "p_placeholder",
  url: PLACEHOLDER_IMAGE_URL,
});

export type ProductImageOrVideoViewState = ReturnType<
  typeof useProductImageOrVideoViewState
>;

export const useProductImageOrVideoViewState = (
  initialImages?: IImage[],
  initialVideos?: IProductVideo[]
) => {
  const [validImages, setValidImages] = useState<IImage[] | undefined>(() => {
    const internal = initialImages?.filter((i) => i.url && isFastly(i.url));
    return internal?.length ? internal : undefined;
  });

  const videos = useMemo<IProductVideo[]>(
    () =>
      initialVideos && canShowVideoProducts()
        ? initialVideos.filter((v) => isNotUndefined(v.video?.url))
        : [],
    [initialVideos]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const images = useMemo(() => {
    const imagesOrPlaceholder = (imgs: IImage[] | undefined): IImage[] =>
      !imgs || isEmpty(imgs) ? [PLACEHOLDER_IMAGE] : imgs;
    return imagesOrPlaceholder(validImages).filter((i) =>
      isNotUndefined(i.url)
    );
  }, [validImages]);

  const itemsCount = images.length + videos.length;

  const isSelectionAnImage = useMemo(
    () => selectedIndex < images.length,
    [selectedIndex, images]
  );

  const getProductMedia = useCallback((): IShowroomProductMedia[] => {
    const imageMedia = images.map((image) => ({
      isVideo: false as const,
      image,
    }));

    const videoMedia = videos
      .map((productVideo) => {
        const video = productVideo.video;
        if (video?.thumbnail_image) {
          return {
            isVideo: true as const,
            video,
          };
        } else {
          return undefined;
        }
      })
      .filter(isNotUndefined);

    const [firstImage, ...restImages] = imageMedia;
    const [firstVideo, ...restVideos] = videoMedia;
    return [firstImage, firstVideo, ...restImages, ...restVideos]
      .filter(isNotUndefined)
      .map((media, index) => ({ ...media, index }));
  }, [images, videos]);

  const selectedImage = useMemo(
    () => (isSelectionAnImage ? images[selectedIndex] : undefined),
    [isSelectionAnImage, images, selectedIndex]
  );

  const selectedVideo = useMemo(
    () =>
      !isSelectionAnImage ? videos[selectedIndex - images.length] : undefined,
    [isSelectionAnImage, videos, selectedIndex, images.length]
  );

  const setImages = useCallback((newImages: IImage[]) => {
    filterInvalidImages(newImages).then(setValidImages);
  }, []);

  const setIndex = useCallback(
    (index: number) => {
      if (index < 0) {
        setSelectedIndex(itemsCount - 1);
      } else if (index >= itemsCount) {
        setSelectedIndex(0);
      } else {
        setSelectedIndex(index);
      }
    },
    [itemsCount]
  );

  return {
    selectedIndex,
    isSelectionAnImage,
    images,
    videos,
    getProductMedia,
    selectedImage,
    selectedVideo,
    setImages,
    setIndex,
  };
};
